import { useCallback, useRef, useEffect } from 'react';
import { notification } from 'antd';

/**
 * Custom hook that manages 30-second recurring notifications
 * for long-running AI note generation.
 *
 * Used by both ManualNotes (when user clicks "Generate AI Notes")
 * and SessionRightPanel (when user returns to a session with in-progress generation).
 *
 * Both consumers share the same notification key ('ai-note-generation-progress'),
 * so notifications never stack — the latest one replaces the previous.
 */

const GENERATION_NOTIFICATIONS_ENABLED = true; // Periodic notifications enabled

const GENERATION_NOTIFY_INTERVAL = 30000; // 30 seconds

export const useGenerationProgressNotifications = () => {
  const generationTimerRef = useRef(null);

  // Start recurring 30-second notifications while AI note generation is in progress
  const startGenerationProgressNotifications = useCallback(() => {
    if (!GENERATION_NOTIFICATIONS_ENABLED) return; // Hidden for now — re-enable later by setting flag to true

    // Clear any existing timer
    if (generationTimerRef.current) {
      clearInterval(generationTimerRef.current);
      generationTimerRef.current = null;
    }

    generationTimerRef.current = setInterval(() => {
      notification.info({
        key: 'ai-note-generation-progress',
        message: 'AI Note Generation In Progress',
        description: `AI note generation is still in progress. You don't need to wait \u2014 feel free to navigate to another appointment and come back later. Your AI note will be ready when you return.`,
        duration: 25, // Auto-dismiss shortly before next notification arrives
      });
    }, GENERATION_NOTIFY_INTERVAL);
  }, []);

  // Stop the generation progress notifications and dismiss any visible notification
  const stopGenerationProgressNotifications = useCallback(() => {
    if (generationTimerRef.current) {
      clearInterval(generationTimerRef.current);
      generationTimerRef.current = null;
    }
    notification.destroy('ai-note-generation-progress');
  }, []);

  // Cleanup on unmount — prevent leaked intervals
  useEffect(() => {
    return () => {
      if (generationTimerRef.current) {
        clearInterval(generationTimerRef.current);
        generationTimerRef.current = null;
      }
    };
  }, []);

  return {
    startGenerationProgressNotifications,
    stopGenerationProgressNotifications,
  };
};
